import { useState } from 'react';
import { useStore } from '../../store';
import { Plus, Trash2, LayoutTemplate } from 'lucide-react';
import { GenericSection as GenericSectionType, GenericField } from '../../types';
import GenericSection from './GenericSection';

interface DocumentDataPanelProps {
  onSaveAsTemplate?: (sections: GenericSectionType[]) => void;
  onManagePresets?: () => void;
}

export default function DocumentDataPanel({ onSaveAsTemplate, onManagePresets }: DocumentDataPanelProps) {
  const {
    genericSections,
    setGenericSections,
    updateGenericField,
    documentTemplates,
    addNotification,
  } = useStore();

  const [selectedPreset, setSelectedPreset] = useState<string>('');

  const handlePresetChange = (templateId: string) => {
    setSelectedPreset(templateId);
    if (!templateId) return;

    const template = documentTemplates.find((t) => t.id === templateId);
    if (template) {
      setGenericSections(
        template.sections.map((section, index) => ({
          ...section,
          order: index + 1,
          isExpanded: true,
        }))
      );
      addNotification(`Applied template: ${template.name}`, 'success');
    }
  };

  const addSection = () => {
    const newSection: GenericSectionType = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      fields: [],
      isExpanded: true,
      order: genericSections.length + 1,
    };
    setGenericSections([...genericSections, newSection]);
  };

  const deleteSection = (sectionId: string) => {
    setGenericSections(
      genericSections.filter((s) => s.id !== sectionId)
    );
  };

  const toggleSectionExpand = (sectionId: string) => {
    setGenericSections(
      genericSections.map((s) =>
        s.id === sectionId ? { ...s, isExpanded: !s.isExpanded } : s
      )
    );
  };

  const updateSectionTitle = (sectionId: string, newTitle: string) => {
    setGenericSections(
      genericSections.map((s) =>
        s.id === sectionId ? { ...s, title: newTitle } : s
      )
    );
  };

  const addFieldToSection = (sectionId: string, field: GenericField) => {
    setGenericSections(
      genericSections.map((s) =>
        s.id === sectionId
          ? { ...s, fields: [...s.fields, field] }
          : s
      )
    );
  };

  const deleteFieldFromSection = (sectionId: string, fieldId: string) => {
    setGenericSections(
      genericSections.map((s) =>
        s.id === sectionId
          ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) }
          : s
      )
    );
  };

  const updateFieldConfig = (
    sectionId: string,
    fieldId: string,
    updates: Partial<GenericField>
  ) => {
    setGenericSections(
      genericSections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId ? { ...f, ...updates } : f
              ),
            }
          : s
      )
    );
  };

  const handleSaveTemplate = () => {
    if (onSaveAsTemplate) {
      onSaveAsTemplate(genericSections);
    }
  };

  return (
    <div className="space-y-4">
      {/* Template Preset Selector */}
      <div className="pb-3 border-b border-gray-100">
        <label className="block text-xs font-medium text-gray-600 mb-1.5">
          Document Data Preset
        </label>
        <div className="flex gap-2">
          <select
            value={selectedPreset}
            onChange={(e) => handlePresetChange(e.target.value)}
            className="flex-1 px-2.5 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent bg-white"
          >
            <option value="">Select preset...</option>
            {documentTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} {template.isCustom ? '(Custom)' : ''}
              </option>
            ))}
          </select>
          {genericSections.length > 0 && onSaveAsTemplate && (
            <button
              onClick={handleSaveTemplate}
              className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:text-accent-dark border border-accent/30 rounded-md hover:bg-accent/10 transition-colors"
              title="Save current layout as template"
            >
              <LayoutTemplate size={12} />
              Save
            </button>
          )}
          {onManagePresets && (
            <button
              onClick={onManagePresets}
              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
              title="Manage presets"
            >
              <LayoutTemplate size={12} />
              Manage
            </button>
          )}
        </div>
      </div>

      {/* Generic Sections */}
      <div className="space-y-3">
        {genericSections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <GenericSection
              key={section.id}
              id={section.id}
              title={section.title}
              fields={section.fields}
              isExpanded={section.isExpanded}
              onToggleExpand={() => toggleSectionExpand(section.id)}
              onTitleChange={(newTitle) => updateSectionTitle(section.id, newTitle)}
              onFieldUpdate={(fieldId, value) =>
                updateGenericField(section.id, fieldId, value)
              }
              onFieldAdd={(field) => addFieldToSection(section.id, field)}
              onFieldDelete={(fieldId) =>
                deleteFieldFromSection(section.id, fieldId)
              }
              onFieldConfigChange={(fieldId, updates) =>
                updateFieldConfig(section.id, fieldId, updates)
              }
              onDeleteSection={() => deleteSection(section.id)}
            />
          ))}
      </div>

      {/* Add Section Button */}
      <button
        onClick={addSection}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-600 hover:text-accent border-2 border-dashed border-gray-300 hover:border-accent rounded-lg transition-colors"
      >
        <Plus size={14} />
        Add Section
      </button>

      {/* Clear All */}
      {genericSections.length > 0 && (
        <button
          onClick={() => {
            if (window.confirm('Clear all sections? This cannot be undone.')) {
              setGenericSections([]);
            }
          }}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs text-red-500 hover:text-red-600 transition-colors"
        >
          <Trash2 size={12} />
          Clear All Sections
        </button>
      )}
    </div>
  );
}
